import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';

export type TooltipVariant = 'default' | 'success' | 'warning' | 'error';

/**
 * Frosted-glass tooltip bubble. `variant` selects the tint — default (accent
 * color), success, warning, error — each keyed to the matching `--*-color`
 * CSS variable, so it stays correct in light and dark themes. An optional
 * `title` line renders above the body.
 *
 * The body is either the `text` input or arbitrary projected content:
 * anything passed via `<ng-content>` wins and `text` is ignored. Projected
 * content without a `title` is centred with equal padding on all sides.
 *
 * `text` is an input rather than projection-only on purpose: the `andorTooltip`
 * trigger directive (tooltip.directive.ts) creates this component
 * programmatically and drives it via `setInput()`, which `<ng-content>` cannot
 * support. All inputs are rendered verbatim — pass already-translated strings.
 */
@Component({
  selector: 'andor-tooltip',
  standalone: true,
  templateUrl: './tooltip.html',
  styleUrl: './tooltip.scss',
  host: {
    class: 'tooltip',
    role: 'tooltip',
    '[class.tooltip-success]': "variant() === 'success'",
    '[class.tooltip-warning]': "variant() === 'warning'",
    '[class.tooltip-error]': "variant() === 'error'",
    '[class.tooltip-bare-content]': 'hasCustomContent() && !title()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Tooltip {
  text = input<string | null>(null);
  title = input<string | null>(null);
  variant = input<TooltipVariant>('default');

  private readonly contentWrapper = viewChild.required<ElementRef<HTMLElement>>('contentWrapper');

  protected readonly hasCustomContent = signal(false);

  constructor() {
    // Whether content was projected is only knowable from the rendered DOM.
    // Anchor comments left by @if/@for in the projected markup must not count
    // as content, hence the element / non-blank-text filter.
    afterRenderEffect(() => {
      const nodes = this.contentWrapper().nativeElement.childNodes;
      this.hasCustomContent.set(
        Array.from(nodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE ||
            (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== '')
        )
      );
    });
  }
}
